import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Loader2, Lock, Pencil, Plus, Save, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { PlatformViewBanner, ManageTenantBtn, TenantPicker, TenantColHeader, TenantColCell, applyTenantFilter, usePlatformView } from "@/components/platform-view/PlatformViewShell";
import { PermLabel } from "@/utils/permissions";

type Role = { id: string; code: string; name: string; description?: string | null; is_system: boolean; permissions: string[]; tenant_id: string | null };

export default function Roles() {
    const { t } = useTranslation();
    const { hasPerm, activeMembership } = useAuth();
    const { filterTenantId } = usePlatformView();
    const [roles, setRoles] = useState<Role[]>([]);
    const [catalog, setCatalog] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [editing, setEditing] = useState<Role | null>(null);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState<Role | null>(null);
    const canCreate = hasPerm("role.create");
    const canEdit = hasPerm("role.edit");
    const canDelete = hasPerm("role.archive");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterTenantId) params.set("filter_tenant_id", filterTenantId);
            const qs = params.toString();
            const [rs, cat] = await Promise.all([
                api.get(`/roles${qs ? `?${qs}` : ""}`),
                api.get(`/roles/permissions-catalog${qs ? `?${qs}` : ""}`),
            ]);
            setRoles(rs.data.items);
            setCatalog(cat.data.permissions);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [filterTenantId]);

    return (
        <div className="space-y-6" data-testid="roles-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("roles.title")}</h1>
                    <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{roles.length}</p>
                </div>
                {canCreate && (
                    <button data-testid="roles-new-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                        <Plus size={16} /> {t("roles.new")}
                    </button>
                )}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            <PlatformViewBanner />

            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" size={22} /></div> : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                                <TenantColHeader />
                                <th className="px-4 py-2 font-medium">{t("roles.code")}</th>
                                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                                <th className="px-4 py-2 font-medium">{t("roles.permission_count")}</th>
                                <th className="px-4 py-2 font-medium text-right">{t("common.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map((r) => (
                                <tr key={r.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`role-row-${r.code}`}>
                                    <td className="px-4 py-3" style={{ color: "var(--text)" }}>{r.is_system ? t(`role.${r.code}` as any, { defaultValue: r.name }) : r.name}</td>
                                    <TenantColCell tenantId={r.tenant_id} testId={`tenant-cell-role-${r.code}`} />
                                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{r.code}</td>
                                    <td className="px-4 py-3">
                                        <span className={`status-pill ${r.is_system ? "status-info" : "status-neutral"}`}>
                                            <ShieldCheck size={12} />
                                            {r.is_system ? t("roles.system") : t("roles.custom")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{r.permissions.length}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <ManageTenantBtn tenantId={r.tenant_id} testId={`manage-row-${r.code}`} nextPath="/settings/roles" />
                                            <RoleRowActions role={r} own={activeMembership?.role?.code === r.code} canEdit={canEdit} canDelete={canDelete}
                                                onView={() => setEditing(r)} onDelete={() => setDeleting(r)} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {(editing || creating) && (
                <RoleDialog role={editing} catalog={catalog} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
            )}
            {deleting && <DeleteRoleDialog role={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />}
        </div>
    );
}

function RoleRowActions({ role, own, canEdit, canDelete, onView, onDelete }: { role: Role; own: boolean; canEdit: boolean; canDelete: boolean; onView: () => void; onDelete: () => void }) {
    const { t } = useTranslation();
    const editable = !role.is_system && !own && canEdit;
    const deletable = !role.is_system && !own && canDelete;
    const btn = "inline-flex items-center justify-center rounded-md p-1.5 hover:bg-[var(--input)]";
    return (
        <>
            {editable
                ? <button data-testid={`role-edit-${role.code}`} title={t("roles.edit")} onClick={onView} className={btn} style={{ color: "var(--primary)" }}><Pencil size={15} /></button>
                : <button data-testid={`role-view-${role.code}`} title={role.is_system ? t("roles.system_locked") : t("roles.view")} onClick={onView} className={btn} style={{ color: "var(--text-muted)" }}>{role.is_system ? <Lock size={15} /> : <Eye size={15} />}</button>}
            {deletable && <button data-testid={`role-delete-${role.code}`} title={t("roles.delete")} onClick={onDelete} className={`${btn} hover:bg-[var(--error-bg)]`} style={{ color: "var(--error-fg)" }}><Trash2 size={15} /></button>}
        </>
    );
}

function DeleteRoleDialog({ role, onClose, onDone }: { role: Role; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const del = async () => {
        setBusy(true);
        try {
            await api.post(`/roles/${role.id}/archive`);
            toast.success(t("roles.delete_success"));
            onDone();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && onClose()} data-testid="role-delete-dialog">
            <div className="surface-card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>{t("roles.delete_title")}</h2>
                <p className="text-sm mb-1" style={{ color: "var(--text)" }}><strong data-testid="role-delete-name">{role.name}</strong> <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>{role.code}</span></p>
                <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{t("roles.delete_body")}</p>
                <div className="flex justify-end gap-2">
                    <button data-testid="role-delete-cancel" disabled={busy} onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{t("common.cancel")}</button>
                    <button data-testid="role-delete-confirm" disabled={busy} onClick={del} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "var(--error-fg)", color: "#fff" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {t("roles.delete")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RoleDialog({ role, catalog, onClose, onSaved }: { role: Role | null; catalog: string[]; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const { activeMembership, switchTenant } = useAuth();
    const { isPlatformView } = usePlatformView();
    const [name, setName] = useState(role?.name || "");
    const [description, setDescription] = useState(role?.description || "");
    const [code, setCode] = useState(role?.code || "");
    const [tenantId, setTenantId] = useState("");
    const [perms, setPerms] = useState<string[]>(role?.permissions || []);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const readonly = role?.is_system || (role && activeMembership?.role?.code === role.code);
    // Platform-view mode has no active membership; permission catalog is
    // unrestricted (platform owner is bypassed server-side).
    const myPerms = new Set(activeMembership?.role?.permissions || catalog);
    const needsTenantPick = !role && isPlatformView;

    const submit = async () => {
        setLoading(true); setErr(null);
        try {
            if (role) {
                await api.patch(`/roles/${role.id}`, { name, description, permissions: perms });
                toast.success(t("roles.saved"));
            } else {
                // Platform-view create: switch into the picked tenant first,
                // then post the create. Server require_perm now has a tenant
                // and duplicate check runs against the correct scope.
                if (needsTenantPick) {
                    if (!tenantId) { setErr(t("platform_view.pick_tenant_required")); setLoading(false); return; }
                    await switchTenant(tenantId);
                }
                await api.post("/roles", { code, name, description: description || null, permissions: perms });
            }
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="role-dialog">
            <div className="surface-card w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{role ? role.name : t("roles.new")}</h2>
                    <button data-testid="role-dialog-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                <div className="p-5 overflow-y-auto space-y-4">
                    {needsTenantPick && (
                        <TenantPicker value={tenantId} onChange={setTenantId} />
                    )}
                    {!role && (
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("roles.name")}</div><input data-testid="role-name-input" value={name} onChange={(e) => setName(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></label>
                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("roles.code")}</div><input data-testid="role-code-input" value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} className="surface-input px-3 py-2 w-full text-sm font-mono" style={{ minHeight: 44 }} /></label>
                        </div>
                    )}
                    {role && !readonly && (
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("roles.name")}</div><input data-testid="role-name-input" value={name} onChange={(e) => setName(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></label>
                    )}
                    {(!readonly || description) && (
                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("roles.description")}</div><textarea data-testid="role-description-input" value={description} disabled={!!readonly} maxLength={1000} rows={2} onChange={(e) => setDescription(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" /></label>
                    )}
                    <div>
                        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("roles.editor_title")}</div>
                        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>{t("roles.no_permission_grant")}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {catalog.map((p) => {
                                const on = perms.includes(p);
                                const grantable = myPerms.has(p);
                                return (
                                    <label key={p} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm" style={{ opacity: !grantable && !readonly ? 0.5 : 1, cursor: readonly || !grantable ? "not-allowed" : "pointer" }}>
                                        <input
                                            type="checkbox"
                                            data-testid={`perm-${p}`}
                                            disabled={!!readonly || !grantable}
                                            checked={on}
                                            onChange={() => setPerms((v) => (on ? v.filter((x) => x !== p) : [...v, p]))}
                                        />
                                        <PermLabel code={p} showCode className="text-sm" />
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }} data-testid="role-error">{err}</div>}
                </div>
                <div className="p-5 border-t" style={{ borderColor: "var(--border)" }}>
                    {!readonly && (
                        <button data-testid="role-save-btn" disabled={loading || !name.trim() || (!role && !code.trim())} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                            {loading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("common.save")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
